// Utility layer (TS-2.5.04) - pure, no side effects. Client-side grouping over the Projects-list
// read path (D-266's scoped v1: "a new Product view page reads projects and products directly
// through the Projects-list data path... grouped by product", not a new Manager composition).
//
// The join/bucket shape itself lives in the shared src/lib/productGrouping.ts (D-277) - this file
// is now a thin, feature-shaped wrapper: it supplies ProjectSummary's own key accessor
// (`productKey`) and renames the generic bucket's `items` back to `projects`, so ProductGroupCard.tsx
// and this file's own pre-existing test suite see no change to ProductGroup's public shape.
//
// UNASSIGNED IS ALWAYS ITS OWN GROUP, LAST, EVEN WHEN EMPTY. D-270 made `productKey` nullable by
// construction, and D-266 expects that today this is likely every project - the empty (or
// near-total) Unassigned bucket is the honest v1 starting state, not a defect to hide.
//
// A PROJECT NAMING A PRODUCT THE CATALOGUE READ DID NOT RETURN IS NOT SILENTLY DROPPED. This
// should not happen when the catalogue is read with includeRetired=true (D-268: retirement never
// deletes a product row), but the grouping does not assume its own caller got that right - a
// project whose productKey matches no known product still needs a place to render, so it falls
// into a synthetic "Unknown product" group rather than vanishing from the count a viewer can see.
import type { ProductRecord, ProjectSummary } from "../../../api/generated/contracts";
import { groupByProduct } from "../../../lib/productGrouping";

export interface ProductGroup {
  /** null for the Unassigned bucket; a placeholder record (isActive: false, no owners) for an unknown key. */
  readonly product: ProductRecord | null;
  readonly isUnknown: boolean;
  readonly projects: readonly ProjectSummary[];
}

export function groupProjectsByProduct(
  projects: readonly ProjectSummary[],
  products: readonly ProductRecord[],
): readonly ProductGroup[] {
  return groupByProduct(projects, products, (project) => project.productKey).map((bucket) => ({
    product: bucket.product,
    isUnknown: bucket.isUnknown,
    projects: bucket.items,
  }));
}
